import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Tabs, Tab, Spinner, Alert, Button, Badge, Form, InputGroup, Modal, Dropdown, ListGroup } from 'react-bootstrap';
import { productApi, agentApi, notificationApi } from '../../services/api';
import { 
  FaLandmark, 
  FaTag, 
  FaRupeeSign, 
  FaCreditCard, 
  FaInfoCircle,
  FaSearch,
  FaFilter,
  FaStar,
  FaRegStar,
  FaArrowRight,
  FaShareAlt,
  FaEnvelope,
  FaTwitter,
  FaFacebookF,
  FaWhatsapp,
  FaTimes,
  FaUser,
  FaSignOutAlt,
  FaBell,
  FaQuestionCircle
} from 'react-icons/fa';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import AgentSidebar from '../../components/AgentSidebar';
import './Products.css';

const AgentProducts = () => {
  const navigate = useNavigate();
  const { getCurrentUser, logout } = useAuth();
  const currentUser = getCurrentUser();
  
  // Products state
  const [productsByCategory, setProductsByCategory] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeKey, setActiveKey] = useState('');
  
  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedBank, setSelectedBank] = useState('');
  const [banks, setBanks] = useState([]);
  const [favoriteProducts, setFavoriteProducts] = useState([]);
  
  // Share modal state
  const [showShareModal, setShowShareModal] = useState(false);
  const [currentProduct, setCurrentProduct] = useState(null);
  const [shareEmail, setShareEmail] = useState('');
  const [shareEmailError, setShareEmailError] = useState('');

  useEffect(() => {
    document.title = 'My Products';
    fetchAgentProducts();
  }, []);

  const fetchAgentProducts = async () => {
    setLoading(true);
    setError('');
    try {
      // Fetch only products assigned to the agent
      const assignedProductsResponse = await agentApi.getAssignedProducts();
      const assignedProducts = assignedProductsResponse.data && assignedProductsResponse.data.items 
        ? assignedProductsResponse.data.items 
        : [];
      
      // If no products are assigned, set error message and return early
      if (assignedProducts.length === 0) {
        setError('No products are assigned to you. Please ask your DSO to assign some products.');
        setLoading(false);
        return;
      }
      
      // Extract all unique banks for filtering
      const uniqueBanks = [...new Set(
        assignedProducts
          .filter(product => product.bank && product.bank.bank)
          .map(product => product.bank.bank)
      )].sort();
      
      setBanks(uniqueBanks);
      
      // Load favorite products from local storage
      const storedFavorites = localStorage.getItem('favoriteProducts');
      if (storedFavorites) {
        setFavoriteProducts(JSON.parse(storedFavorites));
      }
      
      // Group products by category name
      const grouped = assignedProducts.reduce((acc, product) => {
        const categoryName = product.category && product.category.category 
          ? product.category.category 
          : 'Uncategorized';
          
        if (!acc[categoryName]) {
          acc[categoryName] = [];
        }
        acc[categoryName].push(product);
        return acc;
      }, {});
      
      // Add "Favorites" category if there are any favorites
      if (storedFavorites && JSON.parse(storedFavorites).length > 0) {
        const favorites = JSON.parse(storedFavorites);
        grouped['Favorites'] = assignedProducts.filter(product => favorites.includes(product.id));
      }

      setProductsByCategory(grouped);
      
      // Set first category as active by default
      if (Object.keys(grouped).length > 0) {
        setActiveKey(Object.keys(grouped)[0]);
      }

    } catch (err) {
      console.error("Error fetching agent products:", err);
      setError(err.response && err.response.data ? err.response.data.detail : 'Failed to load products.');
    } finally {
      setLoading(false);
    }
  };
  
  const toggleFavorite = (productId) => {
    const updatedFavorites = favoriteProducts.includes(productId)
      ? favoriteProducts.filter(id => id !== productId)
      : [...favoriteProducts, productId];
      
    setFavoriteProducts(updatedFavorites);
    localStorage.setItem('favoriteProducts', JSON.stringify(updatedFavorites));
    
    // Refresh products to update the favorites category
    fetchAgentProducts();
  };
  
  const handleShareClick = (product) => {
    setCurrentProduct(product);
    setShowShareModal(true);
  };

  const handleShareEmailSubmit = (e) => {
    e.preventDefault();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
    if (!emailRegex.test(shareEmail)) {
      setShareEmailError('Please enter a valid email address');
      return;
    }
    
    // Here you would implement the actual email sharing functionality
    console.log(`Sharing ${currentProduct.name} via email to ${shareEmail}`);
    setShareEmail('');
    setShareEmailError('');
    setShowShareModal(false);
  };

  const handleSocialShare = (platform) => {
    if (!currentProduct?.apply_url) return;
    
    let shareUrl = '';
    const text = `Check out this ${currentProduct.name}`;
    const url = encodeURIComponent(currentProduct.apply_url);
    
    switch (platform) {
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${url}`;
        break;
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${url}`;
        break;
      case 'whatsapp':
        shareUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(text + ' ' + currentProduct.apply_url)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank');
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const renderProductCard = (product) => {
    // Parse attributes if it's a JSON string
    let attributesObj = {};
    if (product.attributes && typeof product.attributes === 'string') {
      try {
        attributesObj = JSON.parse(product.attributes);
      } catch (e) {
        console.error("Failed to parse product attributes:", e);
      }
    }
    
    const isFavorite = favoriteProducts.includes(product.id);

    return (
      <Col key={product.id} md={6} lg={4} className="mb-4">
        <Card className="product-card h-100">
          <div className="product-card-header">
            {product.product_image_url && (
              <div className="product-image-container">
                <img 
                  src={product.product_image_url} 
                  alt={product.name} 
                  className="product-image"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = '/static/images/Wishfin-logo.png'; // Fallback image
                  }}
                />
              </div>
            )}
            <Button 
              variant="link" 
              className="favorite-btn" 
              onClick={(e) => {
                e.preventDefault();
                toggleFavorite(product.id);
              }}
            >
              {isFavorite ? <FaStar className="text-warning" /> : <FaRegStar />}
            </Button>
          </div>
          
          <Card.Body className="d-flex flex-column">
            <Card.Title className="product-card-title">{product.name}</Card.Title>
            
            <div className="product-card-meta mb-3">
              {product.bank && product.bank.bank && (
                <Badge bg="light" text="dark" className="meta-badge me-2">
                  <FaLandmark size={14} className="me-1" /> {product.bank.bank}
                </Badge>
              )}
              
              {product.category && product.category.category && (
                <Badge bg="light" text="dark" className="meta-badge">
                  <FaTag size={14} className="me-1" /> {product.category.category}
                </Badge>
              )}
            </div>
            
            <div className="product-details">
              <div className="detail-item">
                <strong>Commission:</strong> 
                {product.commission_percentage ? 
                  `${product.commission_percentage}%` : 
                  (product.commission_amount ? <><FaRupeeSign size={12} /> {product.commission_amount}</> : 'Contact DSO')
                }
              </div>
              
              {attributesObj.interest_rate && (
                <div className="detail-item">
                  <strong>Interest Rate:</strong> {attributesObj.interest_rate}
                </div>
              )}
              
              {attributesObj.fees && (
                <div className="detail-item">
                  <strong>Fees:</strong> {attributesObj.fees}
                </div>
              )}
              
              {Object.keys(attributesObj).length > 0 && (
                <div className="detail-item">
                  <strong>Features:</strong>
                  <div className="attributes-list">
                    {Object.entries(attributesObj)
                      .filter(([key]) => key !== 'interest_rate' && key !== 'fees')
                      .slice(0, 3)
                      .map(([key, value]) => (
                        <span key={key} className="attribute-item">
                          {key}: {value}
                        </span>
                      ))}
                    {Object.keys(attributesObj).length > 3 && (
                      <span className="attribute-item">+more</span>
                    )}
                  </div>
                </div>
              )}
              
              {/* Product description section - handle case when description doesn't exist */}
              <div className="description-container">
                <p className="description-text">
                  {product.product_type ? `Type: ${product.product_type}` : 'No additional details available'}
                </p>
              </div>
            </div>
            
            <div className="mt-auto d-flex justify-content-between align-items-center">
              {product.apply_url && (
                <Button 
                  variant="primary" 
                  href={product.apply_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="me-2"
                >
                  Apply Now
                </Button>
              )}
              
              <Button 
                variant="outline-secondary" 
                onClick={() => handleShareClick(product)}
              >
                <FaShareAlt /> Share
              </Button>
            </div>
          </Card.Body>
        </Card>
      </Col>
    );
  };

  // Notifications state
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const notificationRef = useRef(null);
  
  // Notification handlers
  const toggleNotifications = () => {
    setShowNotifications(prev => !prev);
  };
  
  const handleMarkAsRead = async (notificationId) => {
    const notification = notifications.find(n => n.id === notificationId);
    if (!notification || notification.is_read) return;

    try {
      await notificationApi.markAsRead(notificationId);
      setNotifications(prev => prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };
  
  const handleMarkAllAsRead = async () => {
    if (unreadCount === 0) return;

    try {
      await notificationApi.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };
  
  const formatTimestamp = (timestamp) => {
    try {
      return new Date(timestamp).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
    } catch (e) { return timestamp; }
  };
  
  // Notifications polling effect
  useEffect(() => {
    let intervalId;
    const fetchNotificationsData = async () => {
      try {
        const countResponse = await notificationApi.getUnreadCount();
        setUnreadCount(countResponse.data.unread_count);
      } catch (error) {
        console.error('Error fetching notifications count:', error);
      }
    };

    fetchNotificationsData(); // Initial fetch
    intervalId = setInterval(fetchNotificationsData, 60000); // Poll count every minute
    
    return () => clearInterval(intervalId); // Cleanup
  }, []);

  // Fetch full notifications when dropdown opens
  useEffect(() => {
    const fetchFullNotifications = async () => {
      if (showNotifications) {
        setLoadingNotifications(true);
        try {
          const notificationsResponse = await notificationApi.getNotifications({ limit: 10 });
          setNotifications(notificationsResponse.data);
        } catch (error) {
          console.error('Error fetching notifications:', error);
          setNotifications([]);
        } finally {
          setLoadingNotifications(false);
        }
      }
    };
    fetchFullNotifications();
  }, [showNotifications]);

  // Handle clicks outside notification dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Filter products based on search query and bank filter
  const filterProducts = (products) => {
    if (!products) return [];
    
    return products.filter(product => {
      const matchesSearch = searchQuery === '' || 
        product.name?.toLowerCase().includes(searchQuery.toLowerCase()) || 
        (product.description && product.description.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (product.bank && product.bank.bank && product.bank.bank.toLowerCase().includes(searchQuery.toLowerCase()));
        
      const matchesBank = selectedBank === '' || 
        (product.bank && product.bank.bank === selectedBank);
        
      return matchesSearch && matchesBank;
    });
  };

  const renderContent = () => {
    if (loading) {
      return (
        <Container fluid className="p-4 d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
          <Spinner animation="border" variant="primary" role="status">
            <span className="visually-hidden">Loading Products...</span>
          </Spinner>
        </Container>
      );
    }

    if (error) {
      return (
        <Container fluid className="p-4">
          <Alert variant="danger">{error}</Alert>
        </Container>
      );
    }

    let categories = Object.keys(productsByCategory).sort();
    
    // Move "Favorites" to the front if it exists
    if (categories.includes('Favorites')) {
      categories = ['Favorites', ...categories.filter(cat => cat !== 'Favorites')];
    }

    if (categories.length === 0) {
      return (
        <Container fluid className="p-4">
          <Alert variant="info">
            <h5 className="mb-2">No products available</h5>
            <p className="mb-0">You don't have any products assigned to you yet. Please ask your DSO to assign some products.</p>
          </Alert>
        </Container>
      );
    }

    return (
      <Container fluid className="agent-products-page px-4 py-3">
        <div className="page-header d-flex flex-wrap justify-content-between align-items-center mb-4">
          <h2 className="page-title mb-3 mb-md-0">My Available Products</h2>
        </div>

        <div className="product-tabs-container">
          <Tabs
            activeKey={activeKey}
            onSelect={(k) => setActiveKey(k)}
            className="mb-4 product-category-tabs"
          >
            {categories.map((categoryName) => {
              const filteredProducts = filterProducts(productsByCategory[categoryName]);
              return (
                <Tab 
                  eventKey={categoryName} 
                  title={
                    <span className="tab-title">
                      {categoryName === 'Favorites' && <FaStar className="me-1 text-warning" />}
                      {`${categoryName} (${filteredProducts.length})`}
                    </span>
                  }
                  key={categoryName}
                >
                  {filteredProducts.length === 0 ? (
                    <div className="no-results-message">
                      <p>No products match your search criteria.</p>
                    </div>
                  ) : (
                    <Row>
                      {filteredProducts.map(renderProductCard)}
                    </Row>
                  )}
                </Tab>
              );
            })}
          </Tabs>
        </div>
      </Container>
    );
  };

  return (
    <div className="agent-dashboard-layout">
      {/* Sidebar */}
      <AgentSidebar />
      
      {/* Main Content */}
      <div className="agent-content-wrapper">
        {/* Header */}
        <header className="agent-header">
          <div className="agent-greeting">
            <span className="agent-greeting-text">Hi, {currentUser?.name || 'Agent'} <span className="agent-greeting-emoji">👋</span></span>
          </div>

          <div className="agent-header-search">
            <div className="search-input-container">
              <FaSearch className="search-icon" />
              <input 
                type="text" 
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button className="clear-search-btn" onClick={() => setSearchQuery('')}>
                  <FaTimes />
                </button>
              )}
            </div>
          </div>

          <div className="agent-header-actions">
            <div className="agent-header-icon">
              <FaQuestionCircle size={18} />
            </div>
            
            <div ref={notificationRef} className="position-relative agent-notifications me-3">
              <Button
                variant="link"
                className="text-secondary p-0 position-relative"
                onClick={toggleNotifications}
                id="dropdown-notifications-agent"
                aria-expanded={showNotifications}
                title="Notifications"
              >
                <FaBell size={20} />
                {unreadCount > 0 && (
                  <Badge
                    pill
                    bg="danger"
                    className="position-absolute top-0 start-100 translate-middle border border-light"
                    style={{ fontSize: '0.65em', padding: '0.3em 0.5em' }}
                  >
                    {unreadCount > 9 ? '9+' : unreadCount}
                    <span className="visually-hidden">unread notifications</span>
                  </Badge>
                )}
              </Button>

              {/* Notification Dropdown Menu */}
              <Dropdown.Menu
                show={showNotifications}
                className="shadow mt-2 notification-dropdown"
                style={{ minWidth: '350px', maxHeight: '450px', overflowY: 'auto' }}
              >
                <Dropdown.Header className="d-flex justify-content-between align-items-center">
                  Notifications
                  {unreadCount > 0 && (
                    <Button variant="link" size="sm" onClick={handleMarkAllAsRead} className="p-0 text-decoration-none">
                      Mark all read
                    </Button>
                  )}
                </Dropdown.Header>
                <Dropdown.Divider className="my-1" />
                {loadingNotifications ? (
                  <div className="text-center p-3">
                    <Spinner animation="border" size="sm" variant="primary" /> Loading...
                  </div>
                ) : notifications.length > 0 ? (
                  <ListGroup variant="flush">
                    {notifications.map(notif => (
                      <ListGroup.Item
                        key={notif.id}
                        action={!notif.is_read}
                        onClick={() => handleMarkAsRead(notif.id)}
                        style={{
                          cursor: !notif.is_read ? 'pointer' : 'default',
                          backgroundColor: !notif.is_read ? '#f8f9fa' : 'transparent',
                          borderLeft: !notif.is_read ? '4px solid #0d6efd' : 'none',
                          padding: '0.75rem 1.25rem',
                          borderBottom: '1px solid #eee'
                        }}
                        className="notification-item"
                      >
                        <div className={`fw-bold ${!notif.is_read ? 'text-primary' : ''}`}>{notif.title || 'Notification'}</div>
                        <div className="text-muted small mb-1">{notif.message || 'No details available.'}</div>
                        <small className="text-muted">{formatTimestamp(notif.created_at)}</small>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                ) : (
                  <Dropdown.ItemText className="text-center text-muted p-3">
                    You're all caught up!
                  </Dropdown.ItemText>
                )}
              </Dropdown.Menu>
            </div>
            <div className="agent-user-profile">
              <div className="agent-avatar">
                <FaUser />
              </div>
              <div className="agent-user-info">
                <span className="agent-user-name">
                  {currentUser?.name || 'Agent User'}
                </span>
                <button className="agent-logout-btn" onClick={handleLogout}>
                  <FaSignOutAlt size={14} />
                  <span>Sign out</span>
                </button>
              </div>
            </div>
          </div>
        </header>
        
        {/* Main Content */}
        <div className="agent-main-content">
          {renderContent()}
        </div>
        
        {/* Share Modal */}
        <Modal show={showShareModal} onHide={() => setShowShareModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Share Product</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {currentProduct && (
              <>
                <h5>{currentProduct.name}</h5>
                <p className="mb-4">Share this product with others:</p>
                
                <div className="share-links mb-4 d-flex justify-content-between">
                  <Button 
                    variant="outline-primary" 
                    onClick={() => handleSocialShare('twitter')}
                    className="share-btn"
                  >
                    <FaTwitter /> Twitter
                  </Button>
                  
                  <Button 
                    variant="outline-primary" 
                    onClick={() => handleSocialShare('facebook')}
                    className="share-btn"
                  >
                    <FaFacebookF /> Facebook
                  </Button>
                  
                  <Button 
                    variant="outline-success" 
                    onClick={() => handleSocialShare('whatsapp')}
                    className="share-btn"
                  >
                    <FaWhatsapp /> WhatsApp
                  </Button>
                </div>
                
                <div className="mt-4">
                  <h6>Share via Email</h6>
                  <Form onSubmit={handleShareEmailSubmit}>
                    <Form.Group className="mb-3">
                      <Form.Label>Recipient Email</Form.Label>
                      <Form.Control 
                        type="email" 
                        placeholder="Enter email address" 
                        value={shareEmail}
                        onChange={(e) => setShareEmail(e.target.value)}
                        isInvalid={!!shareEmailError}
                      />
                      <Form.Control.Feedback type="invalid">
                        {shareEmailError}
                      </Form.Control.Feedback>
                    </Form.Group>
                    
                    <Button variant="primary" type="submit">
                      <FaEnvelope className="me-1" /> Send Email
                    </Button>
                  </Form>
                </div>
              </>
            )}
          </Modal.Body>
        </Modal>
      </div>
    </div>
  );
};

export default AgentProducts;
